// A tiny factory-based system for calming ambient sounds, all synthesized
// in-browser via the Web Audio API — no CDN dependency, always offline-safe.
//
// Each preset exposes `build(ctx, output)`:
//   ctx     : AudioContext
//   output  : the destination AudioNode (master gain) new nodes should connect to
// It returns a `cleanup()` function that stops sources and disconnects nodes.

const makeNoiseBuffer = (ctx, kind = "white", seconds = 4) => {
  const bufferSize = seconds * ctx.sampleRate;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);

  if (kind === "brown") {
    let last = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      last = (last + 0.02 * white) / 1.02;
      data[i] = last * 3.5;
    }
  } else if (kind === "pink") {
    // Paul Kellet's pink-noise algorithm.
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.969 * b2 + white * 0.153852;
      b3 = 0.8665 * b3 + white * 0.3104856;
      b4 = 0.55 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.016898;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
  } else {
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
  }
  return buffer;
};

// --- Preset: soft ambient pad (original) ---
const buildPad = (ctx, output) => {
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 900;
  lp.Q.value = 0.4;
  lp.connect(output);

  const freqs = [130.81, 164.81, 196.0, 261.63]; // C3 E3 G3 C4
  const voices = freqs.map((f, i) => {
    const o = ctx.createOscillator();
    o.type = i === 3 ? "triangle" : "sine";
    o.frequency.value = f;
    o.detune.value = (i - 1.5) * 4;

    const g = ctx.createGain();
    g.gain.value = 0.15 / (i + 1);

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05 + i * 0.017;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.04;

    o.connect(g).connect(lp);
    lfo.connect(lfoGain).connect(g.gain);
    o.start();
    lfo.start();
    return { o, g, lfo, lfoGain };
  });

  return () => {
    voices.forEach(({ o, lfo, g, lfoGain }) => {
      try { o.stop(); } catch (_) { /* noop */ }
      try { lfo.stop(); } catch (_) { /* noop */ }
      [o, g, lfo, lfoGain].forEach((n) => { try { n.disconnect(); } catch (_) { /* noop */ } });
    });
    try { lp.disconnect(); } catch (_) { /* noop */ }
  };
};

// --- Preset: ocean waves (brown noise with slow amplitude swells) ---
const buildOcean = (ctx, output) => {
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, "brown", 6);
  src.loop = true;

  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 700;
  lp.Q.value = 0.6;

  const g = ctx.createGain();
  g.gain.value = 0.35;

  // Slow LFO to create wave swells
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.08; // one swell every ~12s
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.25;

  src.connect(lp).connect(g).connect(output);
  lfo.connect(lfoGain).connect(g.gain);

  src.start();
  lfo.start();

  return () => {
    try { src.stop(); } catch (_) { /* noop */ }
    try { lfo.stop(); } catch (_) { /* noop */ }
    [src, lp, g, lfo, lfoGain].forEach((n) => { try { n.disconnect(); } catch (_) { /* noop */ } });
  };
};

// --- Preset: soft rain (pink noise with gentle high shimmer) ---
const buildRain = (ctx, output) => {
  const src = ctx.createBufferSource();
  src.buffer = makeNoiseBuffer(ctx, "pink", 5);
  src.loop = true;

  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 400;

  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 4200;

  const g = ctx.createGain();
  g.gain.value = 0.4;

  // Subtle variance so rain doesn't feel static
  const lfo = ctx.createOscillator();
  lfo.frequency.value = 0.12;
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 0.08;

  src.connect(hp).connect(lp).connect(g).connect(output);
  lfo.connect(lfoGain).connect(g.gain);

  src.start();
  lfo.start();

  return () => {
    try { src.stop(); } catch (_) { /* noop */ }
    try { lfo.stop(); } catch (_) { /* noop */ }
    [src, hp, lp, g, lfo, lfoGain].forEach((n) => { try { n.disconnect(); } catch (_) { /* noop */ } });
  };
};

// --- Preset: deep drone (very low sustained tone, ideal for co-regulation) ---
const buildDrone = (ctx, output) => {
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 400;
  lp.Q.value = 0.5;
  lp.connect(output);

  // Two low voices — a fifth apart, gently drifting
  const freqs = [65.41, 98.0]; // C2, G2
  const voices = freqs.map((f, i) => {
    const o = ctx.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    o.detune.value = (i - 0.5) * 3;

    const g = ctx.createGain();
    g.gain.value = 0.22 - i * 0.05;

    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.03 + i * 0.014;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 0.06;

    o.connect(g).connect(lp);
    lfo.connect(lfoGain).connect(g.gain);
    o.start();
    lfo.start();
    return { o, g, lfo, lfoGain };
  });

  return () => {
    voices.forEach(({ o, lfo, g, lfoGain }) => {
      try { o.stop(); } catch (_) { /* noop */ }
      try { lfo.stop(); } catch (_) { /* noop */ }
      [o, g, lfo, lfoGain].forEach((n) => { try { n.disconnect(); } catch (_) { /* noop */ } });
    });
    try { lp.disconnect(); } catch (_) { /* noop */ }
  };
};

export const SOUND_PRESETS = [
  { id: "pad", name: "ambient pad", build: buildPad },
  { id: "ocean", name: "ocean", build: buildOcean },
  { id: "rain", name: "rain", build: buildRain },
  { id: "drone", name: "deep drone", build: buildDrone },
];

// Handpicked two-sound blends. These are starter defaults — the plan is to
// look at which pairs real users choose most often (once we add opt-in
// anonymous logging) and refine this list from there.
export const CURATED_BLENDS = [
  { id: "coast", name: "coast", primary: "ocean", secondary: "pad" },
  { id: "storm-shelter", name: "storm shelter", primary: "rain", secondary: "drone" },
  { id: "sanctuary", name: "sanctuary", primary: "pad", secondary: "rain" },
  { id: "deep-sea", name: "deep sea", primary: "ocean", secondary: "drone" },
];

export const findMatchingBlend = (primaryId, secondaryId) =>
  CURATED_BLENDS.find(
    (b) => b.primary === primaryId && b.secondary === secondaryId,
  ) || null;

// Soft place-like names for organic pairings that emerge from real usage,
// so popular blends feel like places, not settings. Keyed "primary::secondary".
export const ORGANIC_BLEND_NAMES = {
  "pad::ocean": "shoreline",
  "rain::ocean": "harbour",
  "ocean::rain": "sea mist",
  "rain::pad": "greenhouse",
  "pad::drone": "hearth",
  "drone::pad": "bedrock",
  "drone::ocean": "the deep",
  "drone::rain": "nightfall",
};

// Each blend belongs somewhere. Tapping a blend also washes the screen in a
// fitting light scene (ids from LIGHT_PRESETS). Keyed "primary::secondary".
export const BLEND_SCENES = {
  "pad::ocean": "ocean-shore", // shoreline
  "ocean::pad": "ocean-shore", // coast
  "ocean::rain": "ocean-shore", // sea mist
  "rain::ocean": "ocean-shore", // harbour
  "pad::rain": "forest-rain", // sanctuary
  "rain::pad": "forest-rain", // greenhouse
  "rain::drone": "forest-rain", // storm shelter
  "drone::rain": "night-stars", // nightfall
  "ocean::drone": "night-stars", // deep sea
  "drone::ocean": "aurora", // the deep
  "pad::drone": "sunset", // hearth
  "drone::pad": "mountain-snow", // bedrock
};

export const getSceneForBlend = (primary, secondary) =>
  BLEND_SCENES[`${primary}::${secondary}`] || null;

// Merge live "top blends" data with the handpicked seed list. Popular real
// pairings are promoted to the front; novel pairs get an auto-generated name
// like "rain & drone". If liveTop is empty (no telemetry yet), we fall back
// to the pure seed list so the app always has something to offer.
export const buildEffectiveBlends = (liveTop = [], maxCount = 5) => {
  if (!Array.isArray(liveTop) || liveTop.length === 0) return CURATED_BLENDS;

  const out = [];
  const seen = new Set();

  const addPair = (primary, secondary, matchedBlendId) => {
    if (!primary || !secondary || primary === secondary) return;
    const key = `${primary}::${secondary}`;
    if (seen.has(key)) return;
    seen.add(key);

    const seedByPair = CURATED_BLENDS.find(
      (b) => b.primary === primary && b.secondary === secondary,
    );
    const seedById = matchedBlendId
      ? CURATED_BLENDS.find((b) => b.id === matchedBlendId)
      : null;
    const seed = seedByPair || seedById;

    if (seed) {
      out.push(seed);
    } else {
      const poetic = ORGANIC_BLEND_NAMES[key];
      const p = getSoundPresetById(primary);
      const s = getSoundPresetById(secondary);
      out.push({
        id: `live-${primary}-${secondary}`,
        name: poetic || `${p.name} & ${s.name}`,
        primary,
        secondary,
        source: "live",
      });
    }
  };

  // 1) Popular real pairings first, in count-order.
  liveTop.forEach((row) =>
    addPair(row.primary_id, row.secondary_id, row.blend_id),
  );

  // 2) Any seed blends the users haven't converged on yet still deserve a slot.
  CURATED_BLENDS.forEach((b) => addPair(b.primary, b.secondary, b.id));

  return out.slice(0, maxCount);
};

export const DEFAULT_SOUND_ID = "pad";
export const SOUND_STORAGE_KEY = "stillwater.soundPreset";

export const getSoundPresetById = (id) =>
  SOUND_PRESETS.find((p) => p.id === id) || SOUND_PRESETS[0];

export const getSoundPresetIndex = (id) => {
  const i = SOUND_PRESETS.findIndex((p) => p.id === id);
  return i < 0 ? 0 : i;
};
